/**
 * @fileoverview The season brief for the garden being tended, read once for
 * the screens that lay it out: Your Seasons (the header, the entry, the
 * asks, Recent and coming up and the diary) and the season review page (for a year and
 * the gardener's choice of season; a bed's Lately comes on the bed list since
 * W24 Job H). The
 * backend decides everything (services/season/seasonBrief); this only asks,
 * in the app's language and the gardener's unit. The brief is for the
 * garden's own day (the server's, from its time zone; no day is sent,
 * architecture audit A31); the garden place's day (useGardenPostcode) is in
 * the key, so the brief is read again at the garden's midnight.
 *
 * Read once on an open and kept for a few minutes (SEASON_BRIEF_STALE_MS,
 * W24 Job H; it was read afresh on every visit): a write that changes the
 * garden or the diary marks it stale (shared/lib/briefFreshness gardenWritten
 * and seasonWritten), so a photo just taken is still on the diary at once.
 * The last brief stays on screen while the next is on its way.
 *
 * The account's kept choice of season is not in the key and not sent: the
 * server reads it itself, so the brief is read once on an open whether or not
 * the account's preferences have arrived (they arriving changed the key and
 * read it twice). Only a choice a screen asks for itself (the review page's,
 * while a new one is being saved) is sent and keyed.
 *
 * Where: SeasonPage, SeasonReviewPage.
 * API: GET /season?zip=&lang=&unit=&year=&choice= (getSeason).
 * Settings: SEASON_QUERY_KEY, SEASON_BRIEF_STALE_MS (config/tunables/seasonReview),
 * TODAY_QUERY_RETRIES (config/tunables/today); the choice of season kept
 * on the gardener's account (preferences.seasonReviewBy; audit A72), which
 * the server reads itself when none is sent; the garden's postcode (the
 * garden place's), the gardener's language and temperature unit.
 *
 * @module useSeason
 */

import { retryRule } from '@/config/queryRetry';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { getSeason, type SeasonBrief, type SeasonBy } from '@/shared/api/season';
import { SEASON_BRIEF_STALE_MS, SEASON_QUERY_KEY } from '@/config/tunables/seasonReview';
import { TODAY_QUERY_RETRIES } from '@/config/tunables/today';

/** What a screen asks the brief for. */
export interface UseSeasonOptions {
  /** The season the review page asks for; left out, the season offered. */
  year?: number | null;
  /** What counts as the season, asked for by the screen itself; left out, the one kept on the gardener's account (the server reads it). */
  choice?: SeasonBy | null;
}

/** The brief, and whether it is still coming. */
export interface UseSeason {
  /** The brief, or undefined before the first answer. */
  brief: SeasonBrief | undefined;
  /** True while the first read is on its way. */
  loading: boolean;
  /** The read's failure, or null. */
  error: unknown;
}

/**
 * The season brief for the garden being tended.
 *
 * @param options - The year and the choice of season, where a screen has them.
 * @returns The brief, whether it is still coming, and any failure.
 */
export function useSeason({ year = null, choice }: UseSeasonOptions = {}): UseSeason {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  const { userConfig } = useUserConfig();
  const unit = userConfig.preferences.temperatureUnit;
  const userId = mysqlUser?.user_id ?? null;
  // The garden's postcode and its own day, the server's (the owner's in a shared garden).
  const { zip, date: today, known } = useGardenPostcode();
  // Only a choice the screen asks for is sent and keyed; the kept one is the server's to read.
  const by = choice ?? undefined;
  const { data, isLoading, error } = useQuery({
    queryKey: [SEASON_QUERY_KEY, userId, today, language, unit, year ?? 'offered', by ?? 'kept'],
    queryFn: () => getSeason({ zip, lang: language, unit, year, choice: by }),
    enabled: userId !== null && known && today.length > 0,
    staleTime: SEASON_BRIEF_STALE_MS,
    retry: retryRule(TODAY_QUERY_RETRIES),
    placeholderData: keepPreviousData,
  });
  return { brief: data, loading: isLoading, error: error ?? null };
}
